import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, withNavigationErrorHandler, withViewTransitions } from '@angular/router';

import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { environment } from '@envs/environment';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { authInterceptor } from './shared/interceptors/auth.interceptor';
import { retryInterceptor } from './shared/interceptors/retry.interceptor';

import { GlobalStore } from 'app/store/global.store';
import { TranslateAssetLoader } from 'app/shared/services/translate-asset-loader';
import player from 'lottie-web';
import { provideLottieOptions } from 'ngx-lottie';
import { MARKED_EXTENSIONS, provideMarkdown } from 'ngx-markdown';
import { highlightMarkedExtension } from 'app/shared/markdown/highlight-marked-extension';
import { internalAssetImageMarkedExtension } from 'app/shared/markdown/internal-asset-image-marked-extension';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { skipUnwantedViewTransition } from './shared/utils/view-transition.util';
import { routes } from './app.routes';
import { stompRxServiceFactory, StompService } from './shared/services/stomp.service';
import { DialogService } from 'primeng/dynamicdialog';
import { NavigationDiagnosticsService } from './shared/services/navigation-diagnostics.service';
import { StompSessionService } from './shared/services/stomp-session.service';
import { recoverFromNavigationError } from './shared/services/navigation-error-recovery.service';
import { permissionDeniedInterceptor } from './shared/interceptors/permission-denied.interceptor';

export function httpLoaderFactory(http: HttpClient): TranslateLoader {
  return new TranslateAssetLoader(http);
}

export const appConfig: ApplicationConfig | any = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: 'en',
        loader: {
          provide: TranslateLoader,
          useFactory: httpLoaderFactory,
          deps: [HttpClient],
        },
      })
    ),
    provideAppInitializer(() => {
      const globalStore = inject(GlobalStore);
      globalStore.loadConfiguration();
      globalStore.initLanguage();
    }),
    provideAppInitializer(() => inject(NavigationDiagnosticsService).start()),
    provideAppInitializer(() => {
      inject(StompSessionService);
    }),
    provideRouter(
      routes,
      withNavigationErrorHandler(recoverFromNavigationError),
      withViewTransitions({ skipInitialTransition: true, onViewTransitionCreated: skipUnwantedViewTransition })
    ),
    provideAnimationsAsync(),
    provideHttpClient(
      withInterceptors([authInterceptor, retryInterceptor, permissionDeniedInterceptor])
    ),
    providePrimeNG({
      ripple: true,
      theme: {
        preset: environment.theme.preset,
        options: {
          darkModeSelector: 'none',
          cssLayer: {
            name: 'primeng',
            order: 'inpla-styles, reset, primeng',
          },
        },
      },
    }),
    MessageService,
    DialogService,
    provideMarkdown({
      markedExtensions: [
        { provide: MARKED_EXTENSIONS, multi: true, useValue: highlightMarkedExtension },
        { provide: MARKED_EXTENSIONS, multi: true, useValue: internalAssetImageMarkedExtension },
      ],
    }),
    provideLottieOptions({
      player: () => player,
    }),
    {
      provide: StompService,
      useFactory: stompRxServiceFactory,
    },
  ],
};
